import React from "react";
import { Formik } from "formik";
import { ProductsValidation } from "./ProductsValidation";
import { Drawer, FormControlLabel, Grid, Switch, Typography, Avatar, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import IconButtonIcons from "../../Common/IconButtonIcons";
import Input from "../../Common/Input";
import Button from "../../Common/Button";
import { MultipleFileUploadToServer } from "../../Common/MultipleFileUploadToServer";
import { useDispatch } from "react-redux";

import { setSnackBar } from "../../../store/common/commonSlice";
import { useAddNewProductsMutation, useUpdateProductsMutation } from "../../../store/masters/mastersApis";
import { baseUrl } from "../../../helpers/constants";

export const ProductsAddDrawer = ({ initialValues, show, close, formType }) => {
    const dispatch = useDispatch();
    const [addNewProduct, { isLoading: addProductLoading }] = useAddNewProductsMutation();
    const [updateProduct, { isLoading: updateProductLoading }] = useUpdateProductsMutation();

    const disabled = formType === "View" ? true : false;

    const onFormSubmit = async (values, resetForm) => {
        try {
            // The first gallery image becomes the product's main image (image_url) on the server.
            const { image_url, ...rest } = values;
            let payload = {
                ...rest,
                mrp: values.mrp === "" || values.mrp === undefined ? null : values.mrp,
                video_url: values.video_url ? values.video_url.trim() : null,
                images: values.images || [],
                is_active: values.is_active !== false, // Ensure boolean value
            };

            if (formType === "Edit") {
                await updateProduct(payload).unwrap();
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Product updated successfully",
                        severity: "success",
                    }),
                );
            } else {
                await addNewProduct(payload).unwrap();
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Product created successfully",
                        severity: "success",
                    }),
                );
            }
            close();
            resetForm();
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: error?.data?.message || error.message,
                    severity: "error",
                }),
            );
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{
                name: "",
                image_url: "",
                images: [],
                video_url: "",
                price: "",
                mrp: "",
                description: "",
                is_active: true,
                ...initialValues,
            }}
            onSubmit={(values, { resetForm }) => {
                onFormSubmit(values, resetForm);
            }}
            validationSchema={ProductsValidation}
        >
            {({ handleChange, handleBlur, handleSubmit, values, errors, setFieldValue }) => (
                <Drawer
                    anchor={"right"}
                    open={show}
                    PaperProps={{
                        sx: { width: { xs: "100%", md: "30%", sm: "30%", lg: "30%" } },
                    }}
                    onClose={() => close()}
                >
                    <Grid container sx={{ display: "flex" }} direction={"column"} width={"100%"} height={"100%"}>
                        <Grid
                            container
                            flex={0}
                            px={2}
                            py={1}
                            borderBottom={1}
                            borderColor={"rgba(5, 5, 5, 0.06)"}
                            justifyContent={"space-between"}
                        >
                            <Grid item alignSelf={"center"}>
                                <Typography variant="h6">
                                    {formType === "Add"
                                        ? "Create new Product"
                                        : formType === "Edit"
                                        ? "Update Product"
                                        : "View Product"}
                                </Typography>
                            </Grid>
                            <Grid item alignSelf={"center"}>
                                <IconButtonIcons
                                    color="default"
                                    title="Close"
                                    IconComponent={CloseIcon}
                                    onClick={() => close()}
                                />
                            </Grid>
                        </Grid>
                        <Grid flex={1} px={2} py={2} overflow={"auto"}>
                            <Grid container spacing={2}>
                                {formType !== "Add" && (
                                    <Grid item xs={12} md={12}>
                                        <Input label="ID number" value={values?.id || ""} disabled={true} fullWidth />
                                    </Grid>
                                )}
                                <Grid item xs={12} md={12}>
                                    <Input
                                        label="Product Name"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.name}
                                        disabled={disabled}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Product Images (up to 10). The first image is the cover shown in the shop.
                                    </Typography>
                                    <MultipleFileUploadToServer
                                        key={show ? "open" : "closed"}
                                        value={values.images}
                                        onChange={(images) => setFieldValue("images", images)}
                                        error={Boolean(errors.images)}
                                        helperText={errors.images}
                                        disabled={disabled}
                                        max={10}
                                        appendNew
                                        reorderable
                                        coverLabel="Cover"
                                        previewBaseUrl={baseUrl}
                                        syncValue
                                    />
                                    {/* {values.image_url && (
                                        <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                                            <Avatar
                                                src={baseUrl + values.image_url}
                                                alt="Product Preview"
                                                sx={{ width: 80, height: 80 }}
                                            />
                                        </Box>
                                    )} */}
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Input
                                        label="Product Video (optional)"
                                        name="video_url"
                                        value={values.video_url || ""}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.video_url}
                                        disabled={disabled}
                                        fullWidth
                                        placeholder="https://youtu.be/... or https://www.instagram.com/reel/..."
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Paste a YouTube or Instagram reel/post link. It plays as the first slide of the
                                        product banner in the app.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Input
                                        label="Sale Price"
                                        name="price"
                                        type="number"
                                        value={values.price}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.price}
                                        disabled={disabled}
                                        fullWidth
                                        required
                                        inputProps={{ step: "0.01", min: "0" }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Input
                                        label="MRP (optional)"
                                        name="mrp"
                                        type="number"
                                        value={values.mrp ?? ""}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.mrp}
                                        disabled={disabled}
                                        fullWidth
                                        inputProps={{ step: "0.01", min: "0" }}
                                    />
                                    {values.mrp && values.price && Number(values.mrp) > Number(values.price) ? (
                                        <Typography variant="caption" color="success.main">
                                            Shop shows {Math.round(((values.mrp - values.price) / values.mrp) * 100)}% off
                                        </Typography>
                                    ) : null}
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Input
                                        label="Description"
                                        name="description"
                                        value={values.description}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.description}
                                        disabled={disabled}
                                        fullWidth
                                        multiline
                                        rows={4}
                                        placeholder="Enter product description..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.is_active}
                                                onChange={(e) => setFieldValue("is_active", e.target.checked)}
                                                disabled={disabled}
                                            />
                                        }
                                        label="Active Status"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid flex={0} px={2} py={2} borderTop={1} borderColor={"rgba(5, 5, 5, 0.06)"}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => close()}
                                        disabled={addProductLoading || updateProductLoading}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleSubmit}
                                        loading={addProductLoading || updateProductLoading}
                                        disabled={disabled}
                                    >
                                        {formType === "Add" ? "Create" : formType === "Edit" ? "Update" : "Close"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Drawer>
            )}
        </Formik>
    );
};
